import { Box, Button, Toast } from '@modusign/design-system';
import { useFields } from '@modusign/react-signing';
import { useCallback } from 'react';
import { useIsMutating } from 'react-query';

import { getFieldsErrorMessage } from '@app/libs/fields/getFieldsErrorMessage';

import './TemplateFieldFooter.scss';

interface TemplateFieldFooterProps {
  backButtonText: string;
  error?: boolean;
  nextButtonText: string;
  onBackStepClick: () => void;
  onNextStepClick: () => void;
}

function TemplateFieldFooter({
  backButtonText,
  error,
  nextButtonText,
  onBackStepClick,
  onNextStepClick,
}: TemplateFieldFooterProps) {
  const { fields } = useFields();

  const isUploadingRequesterAttachments =
    useIsMutating({
      exact: true,
      mutationKey: 'requesterAttachmentUpload',
    }) > 0;

  const isNextButtonDisabled = isUploadingRequesterAttachments || error;

  const handleNextButtonClick = useCallback(() => {
    const fieldErrorMessage = getFieldsErrorMessage(fields);
    if (fieldErrorMessage) {
      Toast.warning({ description: fieldErrorMessage });
      return;
    }

    onNextStepClick();
  }, [fields, onNextStepClick]);

  return (
    <Box
      backgroundColor="darkGray700"
      bottom={0}
      color="white"
      left={0}
      p="12px 25px 0px"
      sx={{
        whiteSpace: 'nowrap',
        zIndex: 500, // $z-index-fixed-layout
      }}
      width="100%"
    >
      <Box mb={4} sx={{ minHeight: '38px' }} textAlign="center">
        <Button
          backgroundColor="darkGray500"
          color="lightGray800"
          height={48}
          mr={2}
          sx={{
            borderColor: 'darkGray500',
            '&:hover': {
              backgroundColor: 'darkGray400',
            },
          }}
          width={180}
          onClick={onBackStepClick}
        >
          {backButtonText}
        </Button>
        <Box
          className="TemplateFieldFooter__next-step-button-container"
          display="inline-block"
          position="relative"
          width="180px"
        >
          <Button
            disabled={isNextButtonDisabled}
            height={48}
            variant="primary"
            width={180}
            onClick={handleNextButtonClick}
          >
            {nextButtonText}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export default TemplateFieldFooter;
